// Submissions Page - Für Lead-Generierung & Recruiting Formulare

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/hooks/useAuth';
import { getFormSubmissions, getFormSubmission, updateFormSubmission, deleteFormSubmission, assignSubmissionToCustomer, getCustomers } from '@/lib/api';
import type { FormSubmission, FormType, SubmissionStatus } from '@/lib/types';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import SubmissionsList from '@/components/submissions/SubmissionsList';
import SubmissionDetailModal from '@/components/submissions/SubmissionDetailModal';
import toast from 'react-hot-toast';

export default function SubmissionsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [filterType, setFilterType] = useState<FormType | ''>('');
  const [filterStatus, setFilterStatus] = useState<SubmissionStatus | ''>('');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [submissionToAssign, setSubmissionToAssign] = useState<FormSubmission | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Kunden laden (für Zuweisung)
  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
    enabled: isAdmin,
  });

  // Submissions laden
  const { data: submissions, isLoading } = useQuery({
    queryKey: ['form-submissions', user?.customer_id, filterType, filterStatus, showUnassignedOnly],
    queryFn: () => getFormSubmissions({
      customer_id: isAdmin && !showUnassignedOnly ? undefined : user?.customer_id ?? undefined,
      form_type: filterType || undefined,
      status: filterStatus || undefined,
      unassigned_only: showUnassignedOnly,
    }),
    // Nicht-Admin OHNE Kundenzuordnung darf gar nicht erst laden: Ein leerer
    // customer_id hiess frueher "kein Filter" — zusammen mit der damals
    // RLS-umgehenden View war das das Datenleck vom 04.08.2026. Die DB ist
    // inzwischen dicht (security_invoker), das hier ist der doppelte Boden.
    enabled: !!user && (isAdmin || !!user.customer_id),
  });

  // Einzelne Submission laden (mit Dateien)
  const loadDetailMutation = useMutation({
    mutationFn: (id: string) => getFormSubmission(id),
    onSuccess: (data) => {
      setSelectedSubmission(data);
      setIsDetailModalOpen(true);
    },
    onError: () => {
      toast.error('Fehler beim Laden der Details');
    },
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status: SubmissionStatus; notes?: string } }) =>
      updateFormSubmission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
      toast.success('Status aktualisiert');
      setIsDetailModalOpen(false);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFormSubmission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
      toast.success('Eintrag gelöscht');
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });

  // Assign Mutation
  const assignMutation = useMutation({
    mutationFn: ({ submissionId, customerId }: { submissionId: string; customerId: string }) =>
      assignSubmissionToCustomer(submissionId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
      toast.success('Submission erfolgreich zugewiesen');
      setIsAssignModalOpen(false);
      setSubmissionToAssign(null);
      setSelectedCustomerId('');
    },
    onError: () => {
      toast.error('Fehler beim Zuweisen');
    },
  });

  const handleView = (submission: FormSubmission) => {
    loadDetailMutation.mutate(submission.id);
  };

  const handleUpdateStatus = async (status: SubmissionStatus, notes?: string) => {
    if (!selectedSubmission) return;
    
    await updateMutation.mutateAsync({
      id: selectedSubmission.id,
      data: {
        status,
        notes,
      },
    });
  };

  const handleDelete = (submission: FormSubmission) => {
    deleteMutation.mutate(submission.id);
  };

  const handleOpenAssign = (submission: FormSubmission) => {
    setSubmissionToAssign(submission);
    setIsAssignModalOpen(true);
  };

  const handleAssign = () => {
    if (!submissionToAssign || !selectedCustomerId) {
      toast.error('Bitte wählen Sie einen Kunden');
      return;
    }
    assignMutation.mutate({
      submissionId: submissionToAssign.id,
      customerId: selectedCustomerId,
    });
  };

  const unassignedCount = submissions?.filter(s => !s.customer_id).length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Formular-Einreichungen</h1>
          <p className="text-neutral-500 mt-1">
            {isAdmin ? 'Alle Submissions' : 'Ihre Submissions'} • {submissions?.length || 0} Einträge
            {isAdmin && unassignedCount > 0 && (
              <span className="ml-2 text-red-600 font-medium">
                • {unassignedCount} unzugeordnet
              </span>
            )}
          </p>
        </div>
        {isAdmin && unassignedCount > 0 && (
          <Button
            onClick={() => setShowUnassignedOnly(!showUnassignedOnly)}
            variant={showUnassignedOnly ? 'primary' : 'secondary'}
            size="sm"
          >
            {showUnassignedOnly ? 'Alle anzeigen' : 'Nur unzugeordnete anzeigen'}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Nach Typ filtern"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FormType | '')}
            options={[
              { value: '', label: 'Alle Typen' },
              { value: 'lead_generation', label: 'Lead-Generierung' },
              { value: 'recruiting', label: 'Recruiting' },
            ]}
          />

          <Select
            label="Nach Status filtern"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as SubmissionStatus | '')}
            options={[
              { value: '', label: 'Alle Status' },
              { value: 'new', label: 'Neu' },
              { value: 'processing', label: 'In Bearbeitung' },
              { value: 'completed', label: 'Abgeschlossen' },
              { value: 'archived', label: 'Archiviert' },
            ]}
          />
        </div>
      </div>

      {/* Submissions List */}
      {isAdmin && showUnassignedOnly && submissions && submissions.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Unzugeordnete Submissions</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Diese Submissions sind noch keinem Kunden zugeordnet. Klicken Sie auf "Details" und weisen Sie sie einem Kunden zu.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gruppierte Submissions nach Kunden */}
      {(() => {
        const allSubmissions = submissions || [];
        
        if (isLoading) {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900" />
            </div>
          );
        }

        if (allSubmissions.length === 0) {
          return (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Einträge</h3>
              <p className="mt-1 text-sm text-gray-500">
                Es wurden noch keine Formulare übermittelt
              </p>
            </div>
          );
        }

        // Gruppiere Submissions nach Kunden
        const submissionsByCustomer: Record<string, FormSubmission[]> = {};
        
        allSubmissions.forEach(submission => {
          const customerId = submission.customer_id || 'ohne';
          if (!submissionsByCustomer[customerId]) {
            submissionsByCustomer[customerId] = [];
          }
          submissionsByCustomer[customerId].push(submission);
        });

        // Sortiere Kunden alphabetisch, "ohne Kunde" kommt am Ende
        const sortedCustomerIds = Object.keys(submissionsByCustomer).sort((a, b) => {
          if (a === 'ohne') return 1;
          if (b === 'ohne') return -1;
          const nameA = submissionsByCustomer[a][0]?.customer_name || '';
          const nameB = submissionsByCustomer[b][0]?.customer_name || '';
          return nameA.localeCompare(nameB);
        });

        return (
          <div className="space-y-6">
            {sortedCustomerIds.map(customerId => {
              const customerSubmissions = submissionsByCustomer[customerId];
              const customerName = customerSubmissions[0]?.customer_name || 'Ohne Kunde';

              return (
                <div key={customerId} className="space-y-3">
                  {/* Customer Header */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <h2 className="text-lg font-semibold text-gray-900 px-4">
                      {customerName}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({customerSubmissions.length} {customerSubmissions.length === 1 ? 'Eintrag' : 'Einträge'})
                      </span>
                    </h2>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Submissions for this customer */}
                  <SubmissionsList
                    submissions={customerSubmissions}
                    isLoading={false}
                    onView={handleView}
                    onDelete={isAdmin ? handleDelete : undefined}
                    showCustomer={false}
                  />
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Detail Modal */}
      <SubmissionDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        submission={selectedSubmission}
        onUpdateStatus={isAdmin ? handleUpdateStatus : undefined}
        onAssign={isAdmin ? handleOpenAssign : undefined}
        canEdit={isAdmin}
      />

      {/* Assign Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title="Submission zuweisen"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Wählen Sie einen Kunden aus, dem diese Submission zugeordnet werden soll.
          </p>

          <Select
            label="Kunde"
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            options={[
              { value: '', label: 'Kunde wählen...' },
              ...(customers || []).map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={() => setIsAssignModalOpen(false)}
              disabled={assignMutation.isPending}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleAssign}
              loading={assignMutation.isPending}
              disabled={!selectedCustomerId}
            >
              Zuweisen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
